import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, Activity, TrendingUp, AlertTriangle, ChevronRight, MapPin, Building2, ArrowLeft, Shield, UserCog, UserPlus, Download } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const AdminDashboard = ({ user, onLogout }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [viewLevel, setViewLevel] = useState('national'); // 'national', 'state', 'district'
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [stateDashboard, setStateDashboard] = useState(null);
  const [districtDashboard, setDistrictDashboard] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExportICDS = async () => {
    try {
      setExporting(true);
      const response = await api.exportICDSReport(selectedState, selectedDistrict);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ICDS_ECD_Report_${selectedState || 'All'}_${selectedDistrict || 'All'}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting ICDS report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchStates();
    
    // For State Admin, auto-select their state
    if (user?.role === 'state_admin') {
      // Get state from primary_state or assigned_states
      const stateToSelect = user?.primary_state || (user?.assigned_states && user.assigned_states[0]);
      
      if (stateToSelect) {
        // Auto-select the state admin's state after states are loaded
        setTimeout(() => {
          handleStateSelect(stateToSelect);
        }, 500);
      } else {
        // No state assigned - show error state instead of infinite loading
        console.error('State Admin has no assigned state');
        setLoading(false);
      }
    }
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.getAdminDashboard();
      setDashboard(res.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const res = await api.getStates();
      let stateList = res.data.states || [];
      
      // For State Admin, filter to only their assigned states
      if (user?.role === 'state_admin') {
        const userStates = user?.assigned_states || [];
        const primaryState = user?.primary_state;
        const allowedStates = [...new Set([...userStates, primaryState].filter(Boolean))];
        
        if (allowedStates.length > 0) {
          stateList = stateList.filter(state => allowedStates.includes(state));
        }
      }
      
      setStates(stateList);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const handleStateSelect = async (stateCode) => {
    setSelectedState(stateCode);
    setViewLevel('state');
    setLoading(true);
    
    try {
      // Use the new comprehensive State Admin dashboard API
      const stateRes = await api.getStateAdminDashboard(stateCode);
      setStateDashboard(stateRes.data);
      // Districts are now included in the response as district_breakdown
      setDistricts(stateRes.data.district_breakdown?.map(d => d.district) || []);
    } catch (error) {
      console.error('Error fetching state data:', error);
      // Fallback to old API
      try {
        const [stateRes, districtsRes] = await Promise.all([
          api.getStateDashboard(stateCode),
          api.getDistricts(stateCode)
        ]);
        setStateDashboard(stateRes.data);
        setDistricts(districtsRes.data.districts || []);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        // Set empty dashboard state to prevent infinite loading
        setStateDashboard({
          state_name: stateCode,
          overview: { total_children: 0, total_screenings: 0, coverage_rate: 0, frontline_workers: 0 },
          risk_distribution: { low: 0, moderate: 0, high: 0 },
          district_breakdown: [],
          age_distribution: {},
          recent_high_risk_cases: []
        });
        setDistricts([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDistrictSelect = async (district) => {
    setSelectedDistrict(district);
    setViewLevel('district');
    setLoading(true);
    
    try {
      // Use the new comprehensive District Admin dashboard API
      const res = await api.getDistrictAdminDashboard(selectedState, district);
      setDistrictDashboard(res.data);
    } catch (error) {
      console.error('Error fetching district data:', error);
      // Fallback to old API
      try {
        const res = await api.getDistrictDashboard(selectedState, district);
        setDistrictDashboard(res.data);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (viewLevel === 'district') {
      setViewLevel('state');
      setSelectedDistrict(null);
      setDistrictDashboard(null);
    } else if (viewLevel === 'state') {
      // State Admins should not go back to national view - they stay at state level
      if (user?.role === 'state_admin') {
        // Do nothing - state admins stay at state level
        return;
      }
      setViewLevel('national');
      setSelectedState(null);
      setStateDashboard(null);
      setDistricts([]);
    }
  };

  // Check if user is State Admin - they should not see national view
  const isStateAdmin = user?.role === 'state_admin';
  const isSuperAdmin = user?.role === 'super_admin' || user?.is_super_admin;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-bold text-[#0F4C81]">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  // Render District View with Taluk/Village breakdown
  if (viewLevel === 'district' && districtDashboard) {
    const riskData = [
      { name: t('admin.lowRisk'), value: districtDashboard?.risk_distribution?.low || 0, color: '#10B981' },
      { name: t('admin.moderateRisk'), value: districtDashboard?.risk_distribution?.moderate || 0, color: '#F59E0B' },
      { name: t('admin.highRisk'), value: districtDashboard?.risk_distribution?.high || 0, color: '#EF4444' }
    ];

    // Taluk breakdown data
    const talukBreakdown = districtDashboard?.taluk_breakdown || [];
    const talukChartData = talukBreakdown.slice(0, 8).map(t => ({
      name: t.taluk,
      children: t.total_children,
      highRisk: t.high_risk
    }));

    // Location (City/Village) breakdown
    const locationBreakdown = districtDashboard?.location_breakdown || [];

    // Age distribution
    const ageData = districtDashboard?.age_distribution 
      ? Object.entries(districtDashboard.age_distribution).map(([age, count]) => ({
          name: `${age} yrs`,
          value: count
        }))
      : [];

    return (
      <>
      <div className="min-h-screen bg-[#FAFAF9]" data-testid="admin-district-dashboard">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-[#0F4C81] font-semibold mb-6 hover:underline"
          >
            <ArrowLeft size={20} />
            <span>{t('common.back')}</span>
          </button>

          <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between">
            <div>
              <div className="flex items-center space-x-2 text-sm text-[#6B7280] mb-2">
                {isSuperAdmin && <><span>{t('admin.national')}</span><ChevronRight size={16} /></>}
                <span>{districtDashboard?.state_name || selectedState}</span>
                <ChevronRight size={16} />
                <span className="text-[#0F4C81] font-semibold">{districtDashboard?.district || selectedDistrict}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-2 font-['Nunito']">
                {districtDashboard?.district || selectedDistrict} {t('admin.districtDashboard')}
              </h1>
            </div>
            <div className="mt-4 md:mt-0 flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/user-management')}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#10B981] text-white rounded-xl font-semibold hover:bg-[#059669] transition-colors shadow-md"
                data-testid="user-management-button"
              >
                <UserCog size={18} />
                <span>{t('admin.userManagement')}</span>
              </button>
              <button
                onClick={() => navigate('/audit-logs')}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b] transition-colors shadow-md"
                data-testid="audit-logs-button"
              >
                <Shield size={18} />
                <span>{t('admin.viewAuditLogs')}</span>
              </button>
              <button
                onClick={() => navigate('/admin/create-user')}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#F59E0B] text-white rounded-xl font-semibold hover:bg-[#D97706] transition-colors shadow-md"
                data-testid="create-user-button"
              >
                <UserPlus size={18} />
                <span>Create User</span>
              </button>
            </div>
          </div>

          {/* Enhanced Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100">
              <Users className="text-[#3B82F6] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{districtDashboard?.overview?.total_children || 0}</span>
              <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">Total Children</p>
            </div>
            <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100">
              <Activity className="text-[#10B981] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{districtDashboard?.overview?.total_screenings || 0}</span>
              <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">Screenings</p>
            </div>
            <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100">
              <TrendingUp className="text-[#8B5CF6] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{districtDashboard?.overview?.coverage_rate || 0}%</span>
              <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">Coverage</p>
            </div>
            <div className="rounded-xl bg-red-50 p-5 shadow-sm border-2 border-red-200">
              <AlertTriangle className="text-[#EF4444] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#EF4444]">{districtDashboard?.risk_distribution?.high || 0}</span>
              <p className="text-xs font-semibold text-[#991B1B] uppercase mt-1">High Risk</p>
            </div>
            <div className="rounded-xl bg-yellow-50 p-5 shadow-sm border-2 border-yellow-200">
              <Activity className="text-[#F59E0B] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#F59E0B]">{districtDashboard?.overview?.pending_screenings || 0}</span>
              <p className="text-xs font-semibold text-[#92400E] uppercase mt-1">Pending</p>
            </div>
            <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100">
              <MapPin className="text-[#6366F1] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{districtDashboard?.total_taluks || talukBreakdown.length}</span>
              <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">Taluks</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Risk Distribution */}
            <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100">
              <h3 className="text-xl font-bold text-[#111827] mb-4">{t('admin.riskDistribution')}</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={riskData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label>
                    {riskData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Age Distribution */}
            {ageData.length > 0 && (
              <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100">
                <h3 className="text-xl font-bold text-[#111827] mb-4">Age Distribution</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={ageData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#6366F1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Taluk-wise Chart */}
            {talukChartData.length > 0 && (
              <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100">
                <h3 className="text-xl font-bold text-[#111827] mb-4">Children by Taluk</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={talukChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="children" fill="#0F4C81" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Taluk-wise Analytics Table */}
          {talukBreakdown.length > 0 && (
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-[#111827] mb-4 flex items-center">
                <MapPin className="mr-2 text-[#0F4C81]" size={24} />
                Taluk-wise Analytics
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-bold text-[#374151]">Taluk</th>
                      <th className="text-center py-3 px-4 font-bold text-[#374151]">Children</th>
                      <th className="text-center py-3 px-4 font-bold text-[#374151]">Screenings</th>
                      <th className="text-center py-3 px-4 font-bold text-[#374151]">Coverage</th>
                      <th className="text-center py-3 px-4 font-bold text-[#374151]">High Risk</th>
                      <th className="text-center py-3 px-4 font-bold text-[#374151]">Risk Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {talukBreakdown.map((taluk, index) => (
                      <tr key={taluk.taluk} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="py-3 px-4 font-semibold text-[#111827]">{taluk.taluk}</td>
                        <td className="py-3 px-4 text-center">{taluk.total_children}</td>
                        <td className="py-3 px-4 text-center">{taluk.total_screenings}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            taluk.coverage_rate >= 80 ? 'bg-green-100 text-green-700' :
                            taluk.coverage_rate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {taluk.coverage_rate}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {taluk.high_risk > 0 ? (
                            <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-bold">
                              {taluk.high_risk}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`text-xs font-semibold ${
                            taluk.risk_rate > 20 ? 'text-red-600' :
                            taluk.risk_rate > 10 ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                            {taluk.risk_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* City/Village Breakdown */}
          {locationBreakdown.length > 0 && (
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-[#111827] mb-4 flex items-center">
                <Building2 className="mr-2 text-[#6366F1]" size={24} />
                City/Village-wise Analytics
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto">
                {locationBreakdown.map((location, index) => (
                  <div key={index} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-bold text-[#111827] truncate">{location.city_village}</p>
                      {location.high_risk > 0 && (
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold">
                          {location.high_risk} HR
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#6B7280] mb-2">{location.taluk}</p>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#4B5563]">Children: <strong>{location.total_children}</strong></span>
                      <span className={`font-bold ${
                        location.coverage_rate >= 80 ? 'text-green-600' :
                        location.coverage_rate >= 50 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {location.coverage_rate}% covered
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* High Risk Cases & Recent Screenings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* High Risk Cases */}
            {districtDashboard?.high_risk_cases?.length > 0 && (
              <div className="bg-white rounded-2xl border-2 border-red-100 p-6 shadow-sm">
                <h3 className="text-xl font-bold text-[#111827] mb-4 flex items-center">
                  <AlertTriangle className="mr-2 text-[#EF4444]" size={20} />
                  High-Risk Cases Needing Attention
                </h3>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {districtDashboard.high_risk_cases.map((hrCase, index) => (
                    <div key={index} className="bg-red-50 rounded-lg p-3 border border-red-200">
                      <p className="font-semibold text-[#111827]">{hrCase.child_name}</p>
                      <div className="flex justify-between text-xs text-[#6B7280] mt-1">
                        <span>{hrCase.taluk} • {hrCase.city_village}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Screenings */}
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm">
              <h3 className="text-xl font-bold text-[#111827] mb-4 flex items-center">
                <Activity className="mr-2 text-[#10B981]" size={20} />
                Recent Screenings
              </h3>
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {districtDashboard?.recent_screenings?.slice(0, 10).map((screening, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#F3F4F6] rounded-lg">
                    <div>
                      <p className="font-semibold text-[#111827]">{screening.child_name || 'Unknown'}</p>
                      <p className="text-xs text-[#6B7280]">{screening.taluk} • {screening.city_village}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                      screening.risk_level === 'high' ? 'bg-red-100 text-red-700' :
                      screening.risk_level === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {screening.risk_level?.toUpperCase() || 'N/A'}
                    </span>
                  </div>
                ))}
                {(!districtDashboard?.recent_screenings || districtDashboard.recent_screenings.length === 0) && (
                  <p className="text-center text-[#6B7280] py-4">No recent screenings</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright Footer */}
      <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
        <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
      </footer>
      </>
    );
  }

  // Render State View
  if (viewLevel === 'state' && stateDashboard) {
    const riskData = [
      { name: t('admin.lowRisk'), value: stateDashboard?.risk_distribution?.low || 0, color: '#10B981' },
      { name: t('admin.moderateRisk'), value: stateDashboard?.risk_distribution?.moderate || 0, color: '#F59E0B' },
      { name: t('admin.highRisk'), value: stateDashboard?.risk_distribution?.high || 0, color: '#EF4444' }
    ];

    // Use new district_breakdown or fallback to old district_stats
    const districtBreakdown = stateDashboard?.district_breakdown || [];
    const districtData = districtBreakdown.length > 0 
      ? districtBreakdown.slice(0, 10).map(d => ({
          name: d.district,
          children: d.total_children,
          highRisk: d.high_risk,
          coverage: d.coverage_rate || 0
        }))
      : Object.entries(stateDashboard?.district_stats || {})
          .filter(([_, stats]) => stats.total_children > 0)
          .map(([district, stats]) => ({
            name: district,
            children: stats.total_children,
            highRisk: stats.high_risk
          }))
          .sort((a, b) => b.children - a.children)
          .slice(0, 10);

    // Age distribution data
    const ageData = stateDashboard?.age_distribution 
      ? Object.entries(stateDashboard.age_distribution).map(([age, count]) => ({
          name: `${age} yrs`,
          value: count
        }))
      : [];

    return (
      <>
      <div className="min-h-screen bg-[#FAFAF9]" data-testid="admin-state-dashboard">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Only show back button for Super Admin */}
          {isSuperAdmin && (
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-[#0F4C81] font-semibold mb-6 hover:underline"
            >
              <ArrowLeft size={20} />
              <span>{t('common.back')}</span>
            </button>
          )}

          <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between">
            <div>
              {/* Breadcrumb - hide national for state admin */}
              {isSuperAdmin && (
                <div className="flex items-center space-x-2 text-sm text-[#6B7280] mb-2">
                  <span>{t('admin.national')}</span>
                  <ChevronRight size={16} />
                  <span className="text-[#0F4C81] font-semibold">{stateDashboard?.state_name || selectedState}</span>
                </div>
              )}
              <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-2 font-['Nunito']">
                {stateDashboard?.state_name || selectedState} {t('admin.stateDashboard')}
              </h1>
              {isStateAdmin && (
                <p className="text-lg text-[#6B7280]">Managing {stateDashboard?.state_name || 'State'}</p>
              )}
            </div>
            <div className="mt-4 md:mt-0 flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/user-management')}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#10B981] text-white rounded-xl font-semibold hover:bg-[#059669] transition-colors shadow-md"
                data-testid="user-management-button"
              >
                <UserCog size={18} />
                <span>{t('admin.userManagement')}</span>
              </button>
              <button
                onClick={() => navigate('/audit-logs')}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b] transition-colors shadow-md"
                data-testid="audit-logs-button"
              >
                <Shield size={18} />
                <span>{t('admin.viewAuditLogs')}</span>
              </button>
              <button
                onClick={() => navigate('/admin/create-user')}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#F59E0B] text-white rounded-xl font-semibold hover:bg-[#D97706] transition-colors shadow-md"
                data-testid="create-user-button"
              >
                <UserPlus size={18} />
                <span>Create User</span>
              </button>
              <button
                onClick={handleExportICDS}
                disabled={exporting}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#059669] text-white rounded-xl font-semibold hover:bg-[#047857] transition-colors shadow-md"
                data-testid="icds-export-button"
              >
                <Download size={18} className={exporting ? 'animate-pulse' : ''} />
                <span>{exporting ? 'Exporting...' : 'ICDS Export'}</span>
              </button>
            </div>
          </div>

          {/* Enhanced Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100">
              <Users className="text-[#3B82F6] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{stateDashboard?.overview?.total_children || 0}</span>
              <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">Total Children</p>
            </div>
            <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100">
              <Activity className="text-[#10B981] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{stateDashboard?.overview?.total_screenings || 0}</span>
              <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">Screenings</p>
            </div>
            <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100">
              <TrendingUp className="text-[#8B5CF6] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{stateDashboard?.overview?.coverage_rate || 0}%</span>
              <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">Coverage Rate</p>
            </div>
            <div className="rounded-xl bg-red-50 p-5 shadow-sm border-2 border-red-200">
              <AlertTriangle className="text-[#EF4444] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#EF4444]">{stateDashboard?.risk_distribution?.high || 0}</span>
              <p className="text-xs font-semibold text-[#991B1B] uppercase mt-1">High Risk</p>
            </div>
            <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100">
              <UserCog className="text-[#0F4C81] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{stateDashboard?.overview?.frontline_workers || 0}</span>
              <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">FLW Count</p>
            </div>
            <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100">
              <Building2 className="text-[#6366F1] mb-2" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{stateDashboard?.districts_with_data || districtBreakdown.length}</span>
              <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">Districts</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Risk Distribution */}
            <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100">
              <h3 className="text-xl font-bold text-[#111827] mb-4">{t('admin.riskDistribution')}</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={riskData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label>
                    {riskData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Age Distribution */}
            {ageData.length > 0 && (
              <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100">
                <h3 className="text-xl font-bold text-[#111827] mb-4">Age Distribution</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={ageData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#6366F1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Top Districts by Children */}
            {districtData.length > 0 && (
              <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100">
                <h3 className="text-xl font-bold text-[#111827] mb-4">{t('admin.topDistricts')}</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={districtData.slice(0, 5)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="children" fill="#0F4C81" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* District Breakdown Table */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm mb-8">
            <h2 className="text-2xl font-bold text-[#111827] mb-4 flex items-center">
              <Building2 className="mr-2 text-[#0F4C81]" size={24} />
              District-wise Analytics
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 font-bold text-[#374151]">District</th>
                    <th className="text-center py-3 px-4 font-bold text-[#374151]">Children</th>
                    <th className="text-center py-3 px-4 font-bold text-[#374151]">Screenings</th>
                    <th className="text-center py-3 px-4 font-bold text-[#374151]">Coverage</th>
                    <th className="text-center py-3 px-4 font-bold text-[#374151]">High Risk</th>
                    <th className="text-center py-3 px-4 font-bold text-[#374151]">FLW</th>
                    <th className="text-center py-3 px-4 font-bold text-[#374151]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {districtBreakdown.map((district, index) => (
                    <tr key={district.district} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="py-3 px-4 font-semibold text-[#111827]">{district.district}</td>
                      <td className="py-3 px-4 text-center">{district.total_children}</td>
                      <td className="py-3 px-4 text-center">{district.total_screenings}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          district.coverage_rate >= 80 ? 'bg-green-100 text-green-700' :
                          district.coverage_rate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {district.coverage_rate}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {district.high_risk > 0 ? (
                          <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-bold">
                            {district.high_risk}
                          </span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">{district.frontline_workers || 0}</td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => handleDistrictSelect(district.district)}
                          className="text-[#0F4C81] hover:underline font-semibold text-xs"
                        >
                          View Details →
                        </button>
                      </td>
                    </tr>
                  ))}
                  {districtBreakdown.length === 0 && (
                    <tr>
                      <td colSpan="7" className="py-8 text-center text-gray-500">
                        No district data available yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent High Risk Cases */}
          {stateDashboard?.recent_high_risk_cases?.length > 0 && (
            <div className="bg-white rounded-2xl border-2 border-red-100 p-6 shadow-sm">
              <h2 className="text-2xl font-bold text-[#111827] mb-4 flex items-center">
                <AlertTriangle className="mr-2 text-[#EF4444]" size={24} />
                Recent High-Risk Cases
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stateDashboard.recent_high_risk_cases.slice(0, 6).map((screening, index) => (
                  <div key={index} className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <p className="font-bold text-[#111827]">{screening.child_name || 'Unknown'}</p>
                    <p className="text-sm text-[#6B7280]">{screening.district}</p>
                    {screening.taluk && <p className="text-xs text-[#9CA3AF]">{screening.taluk}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Copyright Footer */}
      <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
        <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
      </footer>
      </>
    );
  }

  // Render National View (Default) - Only for Super Admin
  // State Admins should be auto-redirected to their state view
  if (isStateAdmin && viewLevel === 'national') {
    // If loading is still true, show loading message
    if (loading) {
      return (
        <div className="min-h-screen bg-[#FAFAF9]">
          <Navbar user={user} onLogout={onLogout} />
          <div className="flex items-center justify-center h-96">
            <div className="text-xl font-bold text-[#0F4C81]">{t('common.loading')}</div>
          </div>
        </div>
      );
    }
    
    // If loading is done but no state was selected, show error with state selection
    const userState = user?.primary_state || (user?.assigned_states && user.assigned_states[0]);
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl border-2 border-yellow-200 p-8 text-center">
            <AlertTriangle className="mx-auto text-yellow-500 mb-4" size={48} />
            <h2 className="text-2xl font-bold text-[#111827] mb-4">State Dashboard Loading</h2>
            <p className="text-[#6B7280] mb-6">
              {userState 
                ? `Loading dashboard for ${userState}...`
                : 'No state has been assigned to your account. Please contact your Super Admin.'}
            </p>
            {userState && (
              <button
                onClick={() => handleStateSelect(userState)}
                className="px-6 py-3 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b] transition-colors"
              >
                Retry Loading Dashboard
              </button>
            )}
            {states.length > 0 && (
              <div className="mt-8">
                <p className="text-sm text-[#6B7280] mb-4">Or select your state:</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {states.slice(0, 10).map((state) => (
                    <button
                      key={state}
                      onClick={() => handleStateSelect(state)}
                      className="px-4 py-2 bg-[#F3F4F6] rounded-lg hover:bg-[#E5E7EB] text-sm font-medium"
                    >
                      {state}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const riskChartData = [
    { name: t('admin.lowRisk'), value: dashboard?.risk_distribution?.low || 0, color: '#10B981' },
    { name: t('admin.moderateRisk'), value: dashboard?.risk_distribution?.moderate || 0, color: '#F59E0B' },
    { name: t('admin.highRisk'), value: dashboard?.risk_distribution?.high || 0, color: '#EF4444' }
  ];

  const userChartData = [
    { name: t('auth.caregiver'), value: dashboard?.overview?.caregivers || 0 },
    { name: t('auth.frontlineWorker'), value: dashboard?.overview?.frontline_workers || 0 }
  ];

  return (
    <>
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="admin-dashboard">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-2 font-['Nunito']" data-testid="dashboard-title">
              {t('admin.systemAnalytics')}
            </h1>
            <p className="text-lg text-[#4B5563]">
              {t('admin.comprehensiveInsights')}
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex flex-col md:flex-row gap-3">
            <button
              onClick={() => navigate('/user-management')}
              className="flex items-center space-x-2 px-5 py-3 bg-[#10B981] text-white rounded-xl font-semibold hover:bg-[#059669] transition-colors shadow-md"
              data-testid="user-management-button"
            >
              <UserCog size={20} />
              <span>{t('admin.userManagement')}</span>
            </button>
            <button
              onClick={() => navigate('/audit-logs')}
              className="flex items-center space-x-2 px-5 py-3 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b] transition-colors shadow-md"
              data-testid="audit-logs-button"
            >
              <Shield size={20} />
              <span>{t('admin.viewAuditLogs')}</span>
            </button>
            <button
              onClick={() => navigate('/admin/create-user')}
              className="flex items-center space-x-2 px-5 py-3 bg-[#F59E0B] text-white rounded-xl font-semibold hover:bg-[#D97706] transition-colors shadow-md"
              data-testid="create-user-button"
            >
              <UserPlus size={20} />
              <span>Create User</span>
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="stat-children">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-[#3B82F6]" size={32} />
              <span className="text-3xl font-bold text-[#111827] font-['Nunito']">{dashboard?.overview?.total_children || 0}</span>
            </div>
            <p className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider">{t('dashboard.totalChildren')}</p>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="stat-screenings">
            <div className="flex items-center justify-between mb-2">
              <Activity className="text-[#10B981]" size={32} />
              <span className="text-3xl font-bold text-[#111827] font-['Nunito']">{dashboard?.overview?.total_screenings || 0}</span>
            </div>
            <p className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider">{t('admin.screeningsDone')}</p>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="stat-interventions">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-[#F59E0B]" size={32} />
              <span className="text-3xl font-bold text-[#111827] font-['Nunito']">{dashboard?.overview?.total_interventions || 0}</span>
            </div>
            <p className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider">{t('dashboard.interventions')}</p>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-red-200 bg-red-50" data-testid="stat-high-risk">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="text-[#EF4444]" size={32} />
              <span className="text-3xl font-bold text-[#EF4444] font-['Nunito']">{dashboard?.risk_distribution?.high || 0}</span>
            </div>
            <p className="text-sm font-semibold text-[#991B1B] uppercase tracking-wider">{t('dashboard.highRisk')}</p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="risk-distribution-chart">
            <h3 className="text-2xl font-bold text-[#111827] mb-4 font-['Nunito']">{t('admin.riskDistribution')}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={riskChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {riskChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="user-statistics-chart">
            <h3 className="text-2xl font-bold text-[#111827] mb-4 font-['Nunito']">{t('admin.userStatistics')}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={userChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" stroke="#4B5563" />
                <YAxis stroke="#4B5563" />
                <Tooltip />
                <Bar dataKey="value" fill="#0F4C81" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* State Selection - Only show for Super Admin */}
        {isSuperAdmin && (
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm mb-8">
            <h2 className="text-2xl font-bold text-[#111827] mb-4 font-['Nunito'] flex items-center">
              <MapPin className="mr-2 text-[#0F4C81]" size={24} />
              {t('admin.selectState')}
            </h2>
            <p className="text-[#6B7280] mb-4">{t('admin.drillDownByState')}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 max-h-[300px] overflow-y-auto">
              {states.map((state) => (
                <button
                  key={state}
                  onClick={() => handleStateSelect(state)}
                  className="p-3 text-left bg-[#F3F4F6] rounded-xl hover:bg-[#E5E7EB] transition-all border-2 border-transparent hover:border-[#0F4C81]"
                >
                  <MapPin size={16} className="text-[#0F4C81] mb-1" />
                  <p className="font-semibold text-[#111827] text-sm truncate">{state}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Impact Summary */}
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-8 shadow-sm" data-testid="impact-summary">
          <h2 className="text-3xl font-bold text-[#111827] mb-6 font-['Nunito']">{t('admin.impactSummary')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-[#EFF6FF] rounded-xl border-2 border-[#BFDBFE]">
              <p className="text-lg font-semibold text-[#1E40AF] mb-2">{t('admin.childrenScreened')}</p>
              <p className="text-4xl font-bold text-[#0F4C81] font-['Nunito']">{dashboard?.overview?.total_children || 0}</p>
              <p className="text-sm text-[#4B5563] mt-2">{t('admin.acrossAgeGroups')}</p>
            </div>
            <div className="text-center p-6 bg-[#ECFDF5] rounded-xl border-2 border-[#A7F3D0]">
              <p className="text-lg font-semibold text-[#065F46] mb-2">{t('intervention.title')}</p>
              <p className="text-4xl font-bold text-[#10B981] font-['Nunito']">{dashboard?.overview?.total_interventions || 0}</p>
              <p className="text-sm text-[#4B5563] mt-2">{t('admin.personalizedSupport')}</p>
            </div>
            <div className="text-center p-6 bg-[#FEF3C7] rounded-xl border-2 border-[#FCD34D]">
              <p className="text-lg font-semibold text-[#92400E] mb-2">{t('admin.earlyIdentifications')}</p>
              <p className="text-4xl font-bold text-[#F59E0B] font-['Nunito']">
                {(dashboard?.risk_distribution?.high || 0) + (dashboard?.risk_distribution?.moderate || 0)}
              </p>
              <p className="text-sm text-[#4B5563] mt-2">{t('admin.casesRequiringAttention')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    {/* Copyright Footer */}
    <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
      <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
    </footer>
    </>
  );
};

export default AdminDashboard;
